import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import ConfirmModal from '../../components/ConfirmModal';
import { useConfirm } from '../../components/admin/useConfirm';

type Member = { id: string; name: string; email: string };
type SLesson = { id: string; start: string; durationMinutes: number; topic: string; meetUrl: string; recordingUrl?: string | null };
type TermOption = { id: string; weekday: number; time: string; votes: number; voters: string[] };
type Detail = {
    id: string; name: string; status: number;
    assignedWeekday: number | null; assignedTime: string | null; firstLectureDate: string | null;
    members: Member[]; termOptions: TermOption[]; lessons: SLesson[];
};

// 0 = Monday … 6 = Sunday (matches the backend convention).
const WEEKDAYS = ["Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"];
const STATUS_LABELS = ["Hlasování o termínu", "Čeká na začátek výuky", "Výuka probíhá", "Výuka dokončena"];

const emptyForm = { start: "", durationMinutes: 60, topic: "", meetUrl: "", recordingUrl: "" };

function fmt(d: string) { const x = new Date(d); return x.toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
function fmtTerm(weekday: number, time: string) { return WEEKDAYS[weekday] + " " + time; }

export default function AdminGroupDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = new ApiHelper();

    const [d, setD] = useState<Detail | null>(null);
    const [name, setName] = useState("");
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<Member[]>([]);
    const [form, setForm] = useState({ ...emptyForm });
    const [editId, setEditId] = useState<string | null>(null);
    const [optForm, setOptForm] = useState({ weekday: 0, time: "16:00" });
    const [term, setTerm] = useState({ weekday: 0, time: "16:00", firstLecture: "" });
    const [confirmDel, setConfirmDel] = useState(false);
    const debounce = useRef<ReturnType<typeof setTimeout>>();
    const { ask, confirmModal } = useConfirm();

    useEffect(() => { load(); }, [id]);
    useEffect(() => {
        clearTimeout(debounce.current);
        if (!search.trim()) { setResults([]); return; }
        debounce.current = setTimeout(searchUsers, 300);
        return () => clearTimeout(debounce.current);
    }, [search]);

    async function load() {
        try {
            const data: Detail = await (await api.adminGroupsApi.detail(id!)).json();
            setD(data); setName(data.name);
            setTerm({
                weekday: data.assignedWeekday ?? 0,
                time: data.assignedTime ?? "16:00",
                firstLecture: data.firstLectureDate ? data.firstLectureDate.slice(0, 16) : "",
            });
        } catch (x: any) { api.adminError(x); }
    }
    async function searchUsers() {
        try { setResults(await (await api.adminGroupsApi.searchUsers({ search: search.trim() })).json()); } catch (x: any) { api.adminError(x); }
    }
    async function run(p: Promise<any>, ok: string): Promise<boolean> { try { await p; api.alert.success(ok); await load(); return true; } catch (x: any) { api.adminError(x); return false; } }

    async function saveLesson() {
        if (!form.start) { api.alert.error("Zadej datum a čas"); return; }
        const payload = { start: form.start, durationMinutes: Number(form.durationMinutes), topic: form.topic, meetUrl: form.meetUrl, recordingUrl: form.recordingUrl || null };
        try {
            if (editId) await api.adminGroupsApi.updateLesson(editId, payload);
            else await api.adminGroupsApi.addLesson(id!, payload);
            api.alert.success("Lekce uložena"); setForm({ ...emptyForm }); setEditId(null); await load();
        } catch (x: any) { api.adminError(x); }
    }
    function editLesson(l: SLesson) { setEditId(l.id); setForm({ start: l.start.slice(0, 16), durationMinutes: l.durationMinutes, topic: l.topic, meetUrl: l.meetUrl, recordingUrl: l.recordingUrl ?? "" }); }

    async function addOption() {
        if (!optForm.time) { api.alert.error("Zadej čas"); return; }
        await run(api.adminGroupsApi.addTermOption(id!, { weekday: optForm.weekday, time: optForm.time }), "Termín přidán");
    }

    if (!d) return <div><span className="backLink" onClick={() => navigate("/admin/skupiny")}>← Skupiny</span></div>;

    return (
        <div>
            <span className="backLink" onClick={() => navigate("/admin/skupiny")}>← Skupiny</span>
            <div className="adminHead">
                <div><h1>{d.name}</h1><p>{d.members.length} členů · {STATUS_LABELS[d.status]}</p></div>
                <button className="aBtn danger" onClick={() => setConfirmDel(true)}>Smazat skupinu</button>
            </div>

            <div className="aGrid">
                <div className="aCard">
                    <h2>Název</h2>
                    <div className="inlineRow">
                        <div className="aField inline"><label>Název skupiny</label><input value={name} onChange={e => setName(e.target.value)} /></div>
                        <button className="aBtn" onClick={() => run(api.adminGroupsApi.rename(d.id, { name }), "Přejmenováno")}>Uložit</button>
                    </div>
                </div>

                <div className="aCard">
                    <h2>Stav výuky</h2>
                    <div className="aField"><label>Stav skupiny</label>
                        <select value={d.status} onChange={e => run(api.adminGroupsApi.setStatus(d.id, { status: Number(e.target.value) }), "Stav uložen")}>
                            {STATUS_LABELS.map((s, i) => <option key={i} value={i}>{s}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            <div className="aCard" style={{ marginTop: "18px" }}>
                <h2>Členové ({d.members.length})</h2>
                <div className="rowList">
                    {d.members.map(m => (
                        <div key={m.id} className="lineItem"><span>{m.name || "—"} · {m.email}</span><span className="x" onClick={() => run(api.adminGroupsApi.removeMember(d.id, m.id), "Odebrán")}>×</span></div>
                    ))}
                    {d.members.length === 0 && <span className="empty">Skupina nemá členy.</span>}
                </div>
                <div className="aField" style={{ marginTop: "12px" }}><label>Přidat studenta</label><input placeholder="Hledat podle jména / e-mailu…" value={search} onChange={e => setSearch(e.target.value)} /></div>
                {results.length > 0 &&
                    <div className="rowList">
                        {results.filter(r => !d.members.some(m => m.id === r.id)).map(r => (
                            <div key={r.id} className="lineItem add" onClick={() => { run(api.adminGroupsApi.addMember(d.id, { userId: r.id }), "Přidán"); setSearch(""); setResults([]); }}>
                                <span>{r.name || "—"} · {r.email}</span><span className="plus">+</span>
                            </div>
                        ))}
                    </div>
                }
            </div>

            {/* Term voting: edit the poll options and see the results. */}
            {d.status === 0 &&
                <div className="aCard" style={{ marginTop: "18px" }}>
                    <h2>Hlasování o termínu</h2>
                    <div className="rowList">
                        {d.termOptions.map(o => (
                            <div key={o.id} className="lineItem">
                                <span className="liText">{fmtTerm(o.weekday, o.time)} · <b>{o.votes}</b> {o.votes === 1 ? "hlas" : (o.votes >= 2 && o.votes <= 4 ? "hlasy" : "hlasů")}{o.voters.length > 0 ? " (" + o.voters.join(", ") + ")" : ""}</span>
                                <span className="acts"><span className="actBtn del" onClick={() => ask(<>Smazat termín {fmtTerm(o.weekday, o.time)}?</>, () => run(api.adminGroupsApi.deleteTermOption(o.id), "Termín smazán"))} title="Smazat">×</span></span>
                            </div>
                        ))}
                        {d.termOptions.length === 0 && <span className="empty">Zatím žádné možnosti k hlasování.</span>}
                    </div>
                    <div className="lessonForm">
                        <div className="aField inline"><label>Den</label>
                            <select value={optForm.weekday} onChange={e => setOptForm({ ...optForm, weekday: Number(e.target.value) })}>
                                {WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>)}
                            </select>
                        </div>
                        <div className="aField inline"><label>Čas</label><input type="time" value={optForm.time} onChange={e => setOptForm({ ...optForm, time: e.target.value })} /></div>
                        <button className="aBtn" onClick={addOption}>+ Přidat možnost</button>
                    </div>
                </div>
            }

            {/* Waiting to start: assign the recurring term (weekday + hour) and the
                concrete date of the first lecture. */}
            {d.status === 1 &&
                <div className="aCard" style={{ marginTop: "18px" }}>
                    <h2>Termín výuky</h2>
                    <p className="empty">
                        {d.assignedWeekday != null && d.assignedTime ? "Pravidelný termín: " + fmtTerm(d.assignedWeekday, d.assignedTime) : "Pravidelný termín zatím nebyl přiřazen."}
                        {d.firstLectureDate ? " · První přednáška: " + fmt(d.firstLectureDate) : ""}
                    </p>
                    <div className="lessonForm">
                        <div className="aField inline"><label>Den v týdnu</label>
                            <select value={term.weekday} onChange={e => setTerm({ ...term, weekday: Number(e.target.value) })}>
                                {WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>)}
                            </select>
                        </div>
                        <div className="aField inline"><label>Hodina</label><input type="time" value={term.time} onChange={e => setTerm({ ...term, time: e.target.value })} /></div>
                        <div className="aField inline"><label>První přednáška</label><input type="datetime-local" value={term.firstLecture} onChange={e => setTerm({ ...term, firstLecture: e.target.value })} /></div>
                        <button className="aBtn" onClick={() => run(api.adminGroupsApi.setTerm(d.id, { weekday: term.weekday, time: term.time, firstLectureDate: term.firstLecture || null }), "Termín přiřazen")}>Uložit termín</button>
                        {(d.assignedWeekday != null || d.firstLectureDate) && <button className="aBtn ghost" onClick={() => run(api.adminGroupsApi.setTerm(d.id, { weekday: null, time: null, firstLectureDate: null }), "Termín zrušen")}>Zrušit termín</button>}
                    </div>
                </div>
            }

            {/* Lectures running / done: manage the scheduled lessons (calendar). */}
            {(d.status === 2 || d.status === 3) &&
                <div className="aCard" style={{ marginTop: "18px" }}>
                    <h2>Online lekce</h2>
                    {d.status === 3 && <p className="empty">Výuka je označená jako dokončená. Případné lekce navíc můžeš přidat níže.</p>}
                    <div className="rowList">
                        {d.lessons.map(l => (
                            <div key={l.id} className="lineItem">
                                <span className="liText">{fmt(l.start)} · {l.durationMinutes} min · {l.topic || "(bez tématu)"}{l.recordingUrl ? " · 🎬" : ""}</span>
                                <span className="acts">
                                    <span className="actBtn" onClick={() => editLesson(l)} title="Upravit">✎</span>
                                    <span className="actBtn del" onClick={() => ask(<>Smazat online lekci {fmt(l.start)}?</>, () => run(api.adminGroupsApi.deleteLesson(l.id), "Lekce smazána"))} title="Smazat">×</span>
                                </span>
                            </div>
                        ))}
                        {d.lessons.length === 0 && <span className="empty">Žádné naplánované lekce.</span>}
                    </div>

                    <div className="lessonForm">
                        <div className="aField inline"><label>Datum a čas</label><input type="datetime-local" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} /></div>
                        <div className="aField inline"><label>Délka (min)</label><input type="number" min={1} value={form.durationMinutes} onChange={e => setForm({ ...form, durationMinutes: Number(e.target.value) })} /></div>
                        <div className="aField inline"><label>Téma</label><input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} /></div>
                        <div className="aField inline"><label>Meet URL</label><input placeholder="prázdné = automatický Jitsi odkaz" value={form.meetUrl} onChange={e => setForm({ ...form, meetUrl: e.target.value })} /></div>
                        <div className="aField inline"><label>Záznam URL</label><input value={form.recordingUrl} onChange={e => setForm({ ...form, recordingUrl: e.target.value })} /></div>
                        <button className="aBtn" onClick={saveLesson}>{editId ? "Uložit lekci" : "+ Přidat lekci"}</button>
                        {editId && <button className="aBtn ghost" onClick={() => { setEditId(null); setForm({ ...emptyForm }); }}>Zrušit</button>}
                    </div>
                </div>
            }

            <ConfirmModal open={confirmDel} title="Smazat skupinu?" message={"Skupina „" + d.name + "\" a její naplánované lekce budou smazány; členství studentů zanikne."} confirmLabel="Smazat" cancelLabel="Zrušit" onConfirm={() => { setConfirmDel(false); run(api.adminGroupsApi.remove(d.id), "Skupina smazána").then(ok => { if (ok) navigate("/admin/skupiny"); }); }} onClose={() => setConfirmDel(false)} />
            {confirmModal}
        </div>
    );
}
